import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import type { DesignPage } from '../services/designService';

import './DesignPagePickerModal.css';
import { ModalBackdrop } from './ModalBackdrop';
import { ModalContent } from './ModalContent';
import { ModalHeader } from './ModalHeader';

interface DesignPagePickerModalProps {
  pages: DesignPage[];
  onClose: () => void;
  onSelect: (pageName: string) => void;
}

function useDecodedImage(source?: string): string | null {
  const [decodedSource, setDecodedSource] = useState<string | null>(null);

  // Image lifecycle setup is intentionally kept in one effect so stale loads are cancelled together.
  // eslint-disable-next-line max-statements
  useEffect(() => {
    if (!source) return undefined;

    let active = true;
    const image = new globalThis.Image();
    const reveal = () => {
      if (active) setDecodedSource(source);
    };

    image.decoding = 'async';
    image.onload = () => {
      if (typeof image.decode === 'function') {
        image.decode().then(reveal, () => undefined);

        return;
      }
      reveal();
    };
    image.src = source;

    return () => {
      active = false;
      image.onload = null;
    };
  }, [source]);

  return decodedSource === source ? decodedSource : null;
}

function DesignPageOption({
  page,
  autoFocus,
  onSelect,
}: {
  page: DesignPage;
  autoFocus: boolean;
  onSelect: (pageName: string) => void;
}) {
  const screenshot = page.screenshot_thumb_url || page.screenshot_url;
  const loadedScreenshot = useDecodedImage(screenshot);

  return (
    <li>
      <button
        className="design-page-picker__page"
        type="button"
        onClick={() => onSelect(page.page_name)}
        autoFocus={autoFocus}
      >
        <span
          className={`design-page-picker__preview${loadedScreenshot ? '' : ' design-page-picker__preview--fallback'}`}
          aria-hidden="true"
        >
          {loadedScreenshot
            ? <img src={loadedScreenshot} alt="" />
            : <span className="design-page-picker__initial">{page.page_name.charAt(0).toUpperCase()}</span>}
        </span>
        <span className="design-page-picker__details">
          <span className="design-page-picker__name">{page.page_name}</span>
          <span className="design-page-picker__version">Version {page.version}</span>
        </span>
        <span className="design-page-picker__open" aria-hidden="true">
          <i className="fa-solid fa-arrow-right" />
        </span>
      </button>
    </li>
  );
}

export function DesignPagePickerModal({ pages, onClose, onSelect }: DesignPagePickerModalProps) {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return createPortal(
    <ModalBackdrop isVisible onClick={onClose} className="design-page-picker-backdrop">
      <ModalContent className="design-page-picker" onClose={onClose}>
        <ModalHeader
          title="Choose a page to edit"
          subtitle={`${pages.length} completed pages are ready in the visual editor.`}
          onClose={onClose}
        />
        <ul className="design-page-picker__grid">
          {pages.map((page, index) => (
            <DesignPageOption
              key={page.id}
              page={page}
              autoFocus={index === 0}
              onSelect={onSelect}
            />
          ))}
        </ul>
      </ModalContent>
    </ModalBackdrop>,
    document.body,
  );
}
